import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Navigate } from "react-router-dom";
import { useAccessTier } from "@/hooks/useAccessTier";
import { checkLaunchModuleUnlocked, isApprovedLaunchModuleKey } from "@/hooks/useLaunchModules";

type LaunchModuleAccessContextValue = {
  moduleKey: string | null;
  launchUnlocked: boolean;
};

const LaunchModuleAccessContext = createContext<LaunchModuleAccessContextValue>({
  moduleKey: null,
  launchUnlocked: false,
});

export function useLaunchModuleAccess(moduleKey: string) {
  const access = useContext(LaunchModuleAccessContext);
  return access.launchUnlocked && access.moduleKey === moduleKey;
}

/**
 * Route-level guard for the three beta-curated modules. Admin and paid users
 * always pass. Beta users pass ONLY when the server confirms the module is
 * unlocked. Direct-URL navigation is blocked.
 */
/**
 * Route-level guard for the three beta-curated modules. Admin and paid users
 * always pass. Beta users pass ONLY when the requested module is on the
 * approved beta allowlist; otherwise they are redirected to /dashboard
 * (replace) so back-button cannot return to a locked page.
 */
export default function LaunchModuleGate({
  moduleKey,
  children,
}: {
  moduleKey: string;
  children: ReactNode;
}) {
  const { tier, loading: tierLoading } = useAccessTier();
  const approved = isApprovedLaunchModuleKey(moduleKey);
  const loading = tierLoading;
  const launchExperience = tier !== "admin";
  const contextValue = useMemo(
    () => ({ moduleKey, launchUnlocked: launchExperience && approved }),
    [moduleKey, launchExperience, approved],
  );

  // Server-validated checkpoint enforcement: beta-only learners must have
  // actually unlocked this module (via `beta_module_unlocked` RPC) before
  // the page mounts. Allowlist membership alone is NOT enough — Phase 2B.
  const enforceCheckpoint = tier !== "admin" && approved;
  const [unlockChecked, setUnlockChecked] = useState(false);
  const [serverUnlocked, setServerUnlocked] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!enforceCheckpoint) {
      setUnlockChecked(true);
      setServerUnlocked(false);
      return;
    }
    setUnlockChecked(false);
    void checkLaunchModuleUnlocked(moduleKey).then((ok) => {
      if (cancelled) return;
      setServerUnlocked(ok);
      setUnlockChecked(true);
    });
    return () => { cancelled = true; };
  }, [enforceCheckpoint, moduleKey]);

  if (loading || (enforceCheckpoint && !unlockChecked)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
      </div>
    );
  }

  // Beta users hitting a non-approved module: hard redirect to dashboard.
  // No lock card, no back-button trap.
  if (launchExperience && !approved) {
    return <Navigate to="/dashboard" replace />;
  }

  // Beta users on an approved module that hasn't been unlocked yet:
  // redirect back to the curated beta path (the Dashboard hosts it).
  // Admins and paid users skip this check entirely.
  if (enforceCheckpoint && !serverUnlocked) {
    return <Navigate to="/dashboard" replace />;
  }

  // Approved beta module, OR non-beta user — render through with context.
  return (
    <LaunchModuleAccessContext.Provider value={contextValue}>
      {children}
    </LaunchModuleAccessContext.Provider>
  );
}